import React, { createContext, useState, useRef, useEffect, useCallback } from 'react';
import { registerUser } from '../Api/urlProvider';

export const ChatbotStateContext = createContext();

export const ChatbotStateProvider = ({ children }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [formErrors, setFormErrors] = useState({});
  const [userData, setUserData] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    Course: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [userId, setUserId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const socket = useRef(null);
  const [currentStep, setCurrentStep] = useState('welcome');



  useEffect(() => {
  // Si no hay credenciales o no estamos en 'chat', no conectar.
  if (!userId || !sessionId || currentStep !== 'chat') {
    return;
  }

    if (socket.current && socket.current.readyState === WebSocket.OPEN) {
      console.warn("WebSocket ya está conectado, no se abrirá otra conexión.");
      return;
    }

    const wsUrl = `wss://dam.toolia.site/ws-landing/${userId}/${sessionId}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log("Conectado al WebSocket");
      ws.send(JSON.stringify({
        type: 'init',
        user_id: userId,
        session_id: sessionId,
        companyName: userData.company || "Unknown"
      }));
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log("Mensaje recibido del bot:", data);

      if (data.type === 'pong') return;

      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: 'bot',
          message: data.message,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        },
      ]);
      setIsLoading(false);
    };

    ws.onerror = (error) => {
      console.error("Error en WebSocket:", error);
    };

    ws.onclose = (event) => {
      console.warn("WebSocket cerrado:", event.reason);
      setTimeout(() => {
        if (!socket.current || socket.current.readyState === WebSocket.CLOSED) {
          console.log("Intentando reconectar WebSocket...");
          socket.current = new WebSocket(wsUrl);
        }
      }, 5000); // Intentar reconectar después de 5 segundos
    };

    const pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        console.log("Enviando ping al servidor...");
        ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    socket.current = ws;

    return () => {
      clearInterval(pingInterval);
      ws.close();
    };
  }, [userId, sessionId]);

  const handleSendMessage = useCallback(() => {
    if (!inputValue.trim()) return;

    const message = {
      sender: 'user',
      message: inputValue,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    setMessages((prevMessages) => [...prevMessages, message]); // Agregar mensaje del usuario

    if (socket.current?.readyState === WebSocket.OPEN) {
      console.log("Enviando mensaje:", message);
      socket.current.send(JSON.stringify({ type: 'message', content: inputValue }));
    } else {
      console.error('WebSocket no está conectado.');
    }

    setInputValue('');
    setIsLoading(true);
  }, [inputValue, setMessages, setInputValue, setIsLoading]);


  // Validación del formulario
  const validateForm = () => {
    const errors = {};
    const phoneRegex = /^\+?[1-9]\d{6,14}$/;
    const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/;

    if (!userData.fullName.trim()) {
      errors.fullName = 'El nombre completo es obligatorio.';
    }
    if (!userData.email.trim()) {
      errors.email = 'El correo electrónico es obligatorio.';
    } else if (!emailRegex.test(userData.email)) {
      errors.email = 'El formato del correo electrónico no es válido.';
    }
    if (!userData.phone.trim()) {
      errors.phone = 'El teléfono es obligatorio.';
    } else if (!phoneRegex.test(userData.phone)) {
      errors.phone = 'El teléfono debe ser válido y con formato internacional.';
    }
    if (!userData.company.trim()) {
      errors.company = 'El campo Empresa es obligatorio.';
    }
    if (!userData.Course.trim()) {
      errors.Course = 'Selecciona un tipo de información.';
    }
    if (!captchaVerified) {
      errors.recaptcha = 'Por favor, verifica que no eres un robot.';
    }
    return errors;
  };

  const isFormValid = () => {
    return (
      userData.fullName.trim() !== '' &&
      userData.email.trim() !== '' &&
      userData.phone.trim() !== '' &&
      userData.company.trim() !== '' &&
      userData.Course.trim() !== '' &&
      captchaVerified
    );
  };

  const handleRegisterSubmit = async () => {
    try {
      setIsSubmitted(true);
      const result = await registerUser({
        fullName: userData.fullName,
        email: userData.email,
        phone: userData.phone,
        company: userData.company,
        Course: userData.Course,
      });

      console.log('Usuario registrado:', result);

      // Guardar en el estado
      setUserId(result.user_id);
      setSessionId(result.session_id);
      // Ir al chat
      setCurrentStep('chat');

      // Si aún quieres almacenar userId / sessionId en localStorage, déjalo:
      localStorage.setItem('userId', result.user_id);
      localStorage.setItem('sessionId', result.session_id);
    } catch (error) {
      console.error('Error en el registro:', error.message);
    } finally {
      setIsSubmitted(false);
    }
  };

  useEffect(() => {
    const savedUserId = localStorage.getItem('userId');
    const savedSessionId = localStorage.getItem('sessionId');

    if (savedUserId && savedSessionId) {
      setUserId(savedUserId);
      setSessionId(savedSessionId);
    }

    // Forzar que, cada vez que recarguemos, entremos por 'welcome'
    setCurrentStep('welcome');
  }, []);
  
  



  return (
    <ChatbotStateContext.Provider
      value={{
        messages,
        setMessages,
        inputValue,
        setInputValue,
        isLoading,
        setIsLoading,
        currentStep,
        setCurrentStep,
        formErrors,
        setFormErrors,
        userData,
        setUserData,
        isSubmitted,
        setIsSubmitted,
        captchaVerified,
        setCaptchaVerified,
        socket,
        handleRegisterSubmit,
        handleSendMessage,
        userId,
        setUserId,
        sessionId,
        setSessionId,
      }}
    >
      {children}
    </ChatbotStateContext.Provider>
  );

};
